import React, { useRef, useState, useCallback } from "react";
import { Send, Lock, BellRing, ShieldCheck, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Importe o seu novo componente e a tipagem do Ref (ajuste o caminho se necessário)
import { AutosizeTextarea, AutosizeTextAreaRef } from "@/components/ui/autosize-textarea";

interface TarefaChatInputProps {
  onSend: (mensagem: string) => void;
  isChatDisabled: boolean;
  isConcluido: boolean;
  isSending?: boolean;
}

export const TarefaChatInput = React.memo(
  ({ onSend, isChatDisabled, isConcluido, isSending = false }: TarefaChatInputProps) => {
    const [novaMensagem, setNovaMensagem] = useState("");

    // Usamos a tipagem do SEU componente agora
    const textareaRef = useRef<AutosizeTextAreaRef>(null);

    const handleSendMessage = useCallback(() => {
      const mensagemLimpa = novaMensagem.trim();
      if (!mensagemLimpa || isChatDisabled || isSending) return;

      onSend(mensagemLimpa);
      setNovaMensagem("");

      // Devolve o foco usando a função focus() que você expôs no useImperativeHandle
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, [novaMensagem, isChatDisabled, isSending, onSend]);

    const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNovaMensagem(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (e.key === "Enter" && !e.shiftKey && !isMobile) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    return (
      <div className="relative shrink-0 overflow-hidden border-t bg-background/50 p-4 backdrop-blur-sm">
        {isChatDisabled && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-b-xl bg-background/80 backdrop-blur-[2px]">
            <Lock className="mb-1 h-5 w-5 text-muted-foreground" />
            <span className="px-4 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Acesso Restrito
            </span>
          </div>
        )}

        {!isChatDisabled && isConcluido && (
          <div className="mb-3 flex items-start gap-2 px-1 duration-500 animate-in fade-in">
            <div className="mt-0.5 rounded-full bg-muted p-1">
              <Info className="h-3 w-3 text-muted-foreground" />
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              <strong className="font-semibold text-foreground/80">
                Esta tarefa já foi encerrada.
              </strong>{" "}
              Sua mensagem avisa os envolvidos normalmente, mas não reabre a tarefa.
            </p>
          </div>
        )}

        {!isChatDisabled && !isConcluido && (
          <div className="mb-3 flex items-start gap-2 px-1 duration-500 animate-in fade-in">
            <div className="mt-0.5 rounded-full bg-primary/10 p-1">
              <BellRing className="h-3 w-3 text-primary" />
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              <strong className="font-semibold text-foreground/80">
                Evite notificações excessivas:
              </strong>{" "}
              Tente agrupar todas as informações em uma única mensagem usando{" "}
              <kbd className="rounded border border-border/50 bg-muted px-1 font-sans">
                Shift + Enter
              </kbd>
              .
            </p>
          </div>
        )}

        <div
          className={cn(
            "relative flex items-end gap-2 rounded-xl border border-border bg-muted/30 p-1.5 transition-all",
            !isChatDisabled && "focus-within:bg-card",
            isChatDisabled && "pointer-events-none opacity-50 grayscale-[50%]",
          )}
        >
          <AutosizeTextarea
            ref={textareaRef}
            aria-label="Mensagem da tarefa"
            placeholder="Digite sua mensagem..."
            minHeight={44}
            className="custom-scrollbar w-full resize-none overflow-y-auto border-0 bg-transparent px-3 py-2.5 text-sm shadow-none focus-visible:ring-0"
            value={novaMensagem}
            onChange={handleTextareaInput}
            onKeyDown={handleKeyDown}
            disabled={isChatDisabled || isSending}
          />

          <Button
            size="icon"
            className={cn(
              "h-10 w-10 shrink-0 rounded-lg transition-all",
              novaMensagem.trim() && !isSending
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground hover:bg-muted",
            )}
            onClick={handleSendMessage}
            disabled={!novaMensagem.trim() || isChatDisabled || isSending}
          >
            <span className="sr-only">Enviar mensagem</span>
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="mt-2.5 flex items-center justify-between px-2">
          <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground opacity-70">
            <ShieldCheck className="h-3 w-3" /> Auditado internamente
          </span>
          <span className="flex items-center gap-1 text-[10px] font-medium uppercase text-muted-foreground">
            <span className="rounded-sm border border-border/50 bg-muted px-1.5 py-0.5 shadow-sm">
              Enter
            </span>{" "}
            para enviar
          </span>
        </div>
      </div>
    );
  },
);
TarefaChatInput.displayName = "TarefaChatInput";
